import { useNavigatorApi } from "@/contexts/navigator";
import { useTabs } from "@/contexts/tabsContext";
import { useSessionState } from "@/hooks/useSessionState";
import { useService } from "@/hooks/useService";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { RPC_TIER_GLOBAL } from "@/constants/rpcPriorities";

const PRIORITY_WELCOME = 400;
import { latestVersion, setLatestVersion } from "../changelog/backlog";
import {
  RELEASE_SERVICE_IDENT,
  type IReleaseServicePublic,
} from "@bloomstack/apis/services/IReleaseServicePublic";
import { Button } from "@/components/ui/button";
import { SparklesIcon } from "../dynamicImports";
import { AnimatePresence, motion } from "framer-motion";
import { TabData } from "rc-dock";
import { HomeIcon, LibraryIcon, PlusIcon, SendIcon } from "lucide-react";
import {
  IWelcomeSettingsConfigService,
  WELCOME_SETTINGS_CONFIG_SERVICE_IDENT,
} from "@bloomstack/apis/services/IWelcomeSettingsConfigService";
import {
  IWorkspaceLibraryService,
  WORKSPACE_LIBRARY_SERVICE_IDENT,
} from "@bloomstack/apis/workspace";

/** Welcome tab ID - this tab is shown when no workspaces are open */
export const WELCOME_TAB_ID = "welcome:0";

/** Animated status text shown while lobby is preparing */
const LobbyStatus: FC<{ text: string }> = ({ text }) => (
  <motion.div
    className="text-sm text-zinc-500 dark:text-zinc-400"
    initial={{ opacity: 0, y: 4 }}
    animate={{ opacity: [0.5, 1, 0.5], y: 0 }}
    transition={{ opacity: { duration: 1.5, repeat: Infinity }, y: { duration: 0.2 } }}
  >
    {text}
  </motion.div>
);

/** Welcome tab component */
export const WelcomeTab: FC = () => {
  const nav = useNavigatorApi();
  const tabs = useTabs(false);
  const [lastSeenVersion, setLastSeenVersion] = useSessionState<string>(
    "last-seen-changelog-version",
    "0"
  );

  const welcomeSettingsService = useService<IWelcomeSettingsConfigService>(WELCOME_SETTINGS_CONFIG_SERVICE_IDENT).withPriority("normal", () => RPC_TIER_GLOBAL + PRIORITY_WELCOME);
  const workspaceLibService = useService<IWorkspaceLibraryService>(WORKSPACE_LIBRARY_SERVICE_IDENT).withPriority("normal", () => RPC_TIER_GLOBAL + PRIORITY_WELCOME);
  const releaseService = useService<IReleaseServicePublic>(RELEASE_SERVICE_IDENT).withPriority("normal", () => RPC_TIER_GLOBAL + PRIORITY_WELCOME);

  const [lobbyEnabled, setLobbyEnabled] = useState(false);
  const [lobbyMessage, setLobbyMessage] = useState("");
  const [lobbyStatus, setLobbyStatus] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [currentVersion, setCurrentVersion] = useState(latestVersion);
  const hasNewVersion = currentVersion !== "0" && currentVersion !== lastSeenVersion;

  // Fetch latest version on mount
  useEffect(() => {
    if (latestVersion !== "0") {
      setCurrentVersion(latestVersion);
      return;
    }
    releaseService.getLatestVersion().then((result) => {
      if (result.success) {
        setLatestVersion(result.result);
        setCurrentVersion(result.result);
      }
    });
  }, []);

  // Set tab metadata on mount
  useEffect(() => {
    tabs?.setTabMetadata(WELCOME_TAB_ID, { title: "Welcome", icon: <HomeIcon className="w-4 h-4" /> });
  }, [tabs]);

  // Check if lobby is configured
  useEffect(() => {
    const checkLobby = async () => {
      try {
        const result = await welcomeSettingsService.read();
        console.log("[Lobby] welcomeSettings.read result:", JSON.stringify(result));
        if (result.success && result.result.lobbyTemplateWorkspaceId) {
          setLobbyEnabled(true);
        } else {
          console.warn("[Lobby] lobby input hidden — read() succeeded but lobbyTemplateWorkspaceId is falsy or read failed:", JSON.stringify(result));
        }
      } catch (error) {
        console.error("[Lobby] welcomeSettings.read threw (lobby input will not render):", error);
      }
    };
    checkLobby();
  }, [welcomeSettingsService]);

  const handleViewWorkspaceLibrary = () => {
    nav.open({
      route: "workspace-library",
    });
  };

  const handleCreateWorkspace = () => {
    nav.open({
      route: "workspace-create",
    });
  };

  const handleOpenChangelog = () => {
    tabs?.open("changelog");
    setLastSeenVersion(currentVersion);
  };

  const handleLobbySubmit = useCallback(async () => {
    const trimmed = lobbyMessage.trim();
    if (!trimmed || lobbyStatus) return;

    setLobbyStatus("Preparing your workspace...");

    try {
      const result = await workspaceLibService.prepareLobby(trimmed);
      console.log("[Lobby] prepareLobby result:", JSON.stringify(result));

      if (result.success) {
        setLobbyStatus("Opening workspace...");
        tabs?.open("workspace", result.result.id);
      } else {
        console.warn("[Lobby] prepareLobby failed:", JSON.stringify(result.error));
        setLobbyStatus(null);
        setLobbyMessage(trimmed);
      }
    } catch (error) {
      console.error("[Lobby] prepareLobby threw:", error);
      setLobbyStatus(null);
      setLobbyMessage(trimmed);
    }
  }, [lobbyMessage, lobbyStatus, workspaceLibService, tabs]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleLobbySubmit();
    }
  }, [handleLobbySubmit]);

  return (
    <div className="flex flex-col gap-4 items-center justify-center h-full w-full bg-zinc-100 dark:bg-zinc-900 text-zinc-600 dark:text-zinc-400">
      <div className="text-2xl font-semibold text-black dark:text-white text-center">
        Welcome to Bloomstack
      </div>
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          intent="secondary"
          label="View Workspaces"
          icon={<LibraryIcon size={16} />}
          onClick={handleViewWorkspaceLibrary}
          className="order-2 sm:order-1"
        />

        <Button
          intent="primary"
          label="New Workspace"
          icon={<PlusIcon size={16} />}
          onClick={handleCreateWorkspace}
          className="order-1 sm:order-2"
        />
      </div>

      {/* What's New link */}
      <motion.button
        onClick={handleOpenChangelog}
        className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors mt-2"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <motion.span
          animate={
            hasNewVersion
              ? {
                  rotate: [0, 15, -15, 15, 0],
                  scale: [1, 1.2, 1.2, 1.2, 1],
                }
              : {}
          }
          transition={{
            duration: 0.6,
            repeat: hasNewVersion ? Infinity : 0,
            repeatDelay: 2,
          }}
        >
          <SparklesIcon className="w-4 h-4" />
        </motion.span>
        <span>What's New in v{currentVersion}</span>
        {hasNewVersion && (
          <motion.span
            className="px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 rounded-full"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
          >
            NEW
          </motion.span>
        )}
      </motion.button>

      {/* Lobby input facade */}
      {lobbyEnabled && (
        <div className="flex flex-col items-center gap-2 mt-6 w-full max-w-xl px-4">
          <AnimatePresence mode="wait">
            {lobbyStatus && (
              <LobbyStatus key="status" text={lobbyStatus} />
            )}
          </AnimatePresence>
          <div className="flex items-end w-full gap-2 bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 rounded-lg p-2 shadow-sm focus-within:ring-2 focus-within:ring-blue-400/70 transition-shadow">
            <textarea
              ref={inputRef}
              className="flex-1 resize-none bg-transparent outline-none text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 min-h-[36px] max-h-[120px] py-1.5 px-2"
              placeholder="Ask anything to get started..."
              rows={1}
              value={lobbyMessage}
              onChange={(e) => setLobbyMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!!lobbyStatus}
            />
            <Button
              intent="primary"
              icon={<SendIcon className="w-4 h-4" />}
              onClick={handleLobbySubmit}
              disabled={!lobbyMessage.trim() || !!lobbyStatus}
              className="shrink-0"
            />
          </div>
        </div>
      )}
    </div>
  );
};

/** Renderer for the welcome tab */
export const WelcomeTabRenderer = (id: string): TabData => ({
  id,
  title: <div className="flex items-center gap-1.5 whitespace-nowrap"><HomeIcon className="w-4 h-4" /> Welcome</div>,
  content: <WelcomeTab />,
  closable: true,
  group: "default",
});
